import { assign, cloneDeep, entries, forEach, get, has, isArray, isEmpty, keyBy, map, omit } from 'lodash';

const merge_section_attributes = (
	attributes: any[] | undefined,
	source: Record<string, any> | undefined,
	resolve_value?: (attr_id: string, raw: unknown) => unknown,
) => {
	if (!isArray(attributes) || isEmpty(source)) return;

	forEach(attributes, (attr) => {
		const attr_id = attr?.id;
		if (!has(source, attr_id)) return;
		const raw = get(source, attr_id);
		attr.value = resolve_value ? resolve_value(attr_id, raw) : raw ?? '';
	});
};

const build_attributes_from_template = (template_attrs: any[], fields: Record<string, unknown>) => {
	const template_ids = new Set(map(template_attrs, 'id'));
	const attributes = map(template_attrs, (template_attr) => ({
		...template_attr,
		value: get(fields, template_attr?.id, template_attr?.value ?? ''),
	}));

	forEach(entries(fields), ([key, value]) => {
		if (!template_ids.has(key)) {
			attributes.push({ id: key, value: value ?? '' });
		}
	});

	return attributes;
};

const map_contacts_or_addresses = (values: any[], template: any, map_item: (v2_item: any, template: any) => any) =>
	map(values, (v2_item) => map_item(v2_item, template));

const to_keyed_object = (raw: any) => {
	if (raw === null || raw === undefined) return {};
	if (isArray(raw)) {
		return keyBy(raw, (row: any) => row?.payment_method_id || row?.id || '');
	}
	return typeof raw === 'object' ? raw : {};
};

export const transform_buyer_details_v2_to_sections = (form_data: any, v2_data: any): any => {
	if (!form_data || !form_data.sections) {
		return { sections: [], ...(v2_data || {}) };
	}

	if (!v2_data) {
		return form_data;
	}

	const merged_data = cloneDeep(form_data);
	const sections = merged_data.sections || [];
	const {
		basic_details: v2_basic_details,
		contacts: v2_contacts,
		addresses: v2_addresses,
		preferences: v2_preferences,
		other_details: v2_other_details,
		tax_preferences: v2_tax_preferences,
		payment_methods: v2_payment_methods,
		wizshop_users: v2_wizshop_users,
		lead_details: v2_lead_details,
		wallet_balance,
		reference_id,
		created_at,
		updated_at,
		created_by_name,
		updated_by_name,
		is_sync,
		...rest_top_level_fields
	} = v2_data;

	forEach(sections, (section: any) => {
		const section_key = section?.key;

		if (section_key === 'basic_details' && v2_basic_details) {
			merge_section_attributes(section?.attributes, v2_basic_details);
		}

		if (section_key === 'contacts' && v2_contacts) {
			if (isArray(v2_contacts.values)) {
				const contact_template = get(section, 'contacts[0]', null);

				section.contacts = map_contacts_or_addresses(v2_contacts.values, contact_template, (v2_contact, template) => {
					const { id } = v2_contact;
					const contact_fields = omit(v2_contact, 'id');

					if (template?.attributes) {
						return {
							...template,
							id: id || '',
							attributes: build_attributes_from_template(template?.attributes, contact_fields),
						};
					}

					return {
						id: id || '',
						attributes: map(entries(contact_fields), ([key, value]) => ({
							id: key,
							value: value ?? '',
						})),
					};
				});
			}
			section.primary_contact = v2_contacts?.primary_contact || section?.primary_contact || null;
		}

		if (section_key === 'addresses' && v2_addresses) {
			if (isArray(v2_addresses.values)) {
				const address_template = get(section, 'addresses[0]', null);

				section.addresses = map_contacts_or_addresses(v2_addresses.values, address_template, (v2_address, template) => {
					const { id, type, is_verified } = v2_address;
					const fields_for_lookup = { ...omit(v2_address, ['id', 'is_verified']), type };

					if (template?.attributes) {
						return {
							...template,
							id: id || '',
							type: type || template?.type || null,
							is_verified: is_verified !== undefined ? is_verified : template?.is_verified || false,
							attributes: build_attributes_from_template(template?.attributes, fields_for_lookup),
						};
					}

					return {
						id: id || '',
						type: type || null,
						is_verified: is_verified || false,
						attributes: map(entries(fields_for_lookup), ([key, value]) => ({
							id: key,
							value: value ?? '',
						})),
					};
				});
			}
			section.default_billing_address = v2_addresses?.default_billing_address || section?.default_billing_address || null;
			section.default_shipping_address = v2_addresses?.default_shipping_address || section?.default_shipping_address || null;
		}

		if (section_key === 'preferences' && v2_preferences) {
			merge_section_attributes(section?.attributes, v2_preferences);
		}

		if (section_key === 'other_details' && v2_other_details) {
			if (isArray(v2_other_details?.attachments)) {
				section.attachments = v2_other_details?.attachments;
			}

			if (v2_other_details?.custom_attributes) {
				if (isArray(section?.custom_attributes)) {
					merge_section_attributes(section?.custom_attributes, v2_other_details?.custom_attributes);
				} else {
					section.custom_attributes = map(entries(v2_other_details?.custom_attributes), ([id, value]) => ({
						id,
						value: value ?? '',
					}));
				}
			}
		}

		if (section_key === 'tax_preferences' && v2_tax_preferences) {
			merge_section_attributes(section?.attributes, v2_tax_preferences, (attr_id, raw) =>
				(attr_id === 'is_taxable' && (raw === '' || raw === undefined || raw === null) ? false : raw ?? ''),
			);
		}

		if (section_key === 'payment_methods' && v2_payment_methods) {
			merge_section_attributes(section?.attributes, v2_payment_methods);

			section.saved_payment_methods =
				v2_payment_methods.saved_payment_methods !== null && v2_payment_methods.saved_payment_methods !== undefined
					? to_keyed_object(v2_payment_methods.saved_payment_methods)
					: section?.saved_payment_methods ?? {};
			section.saved_bank_accounts =
				v2_payment_methods.saved_bank_accounts !== null && v2_payment_methods.saved_bank_accounts !== undefined
					? to_keyed_object(v2_payment_methods.saved_bank_accounts)
					: section?.saved_bank_accounts ?? {};
		}

		if (section_key === 'wizshop_users' && v2_wizshop_users) {
			if (isArray(v2_wizshop_users.values)) {
				section.wizshop_users = v2_wizshop_users.values;
			} else if (isArray(v2_wizshop_users)) {
				section.wizshop_users = v2_wizshop_users;
			}
		}

		if (section_key === 'lead_details' && v2_lead_details) {
			merge_section_attributes(section?.attributes, v2_lead_details);
		}
	});

	merged_data.wallet_balance = wallet_balance ?? merged_data.wallet_balance ?? 0;
	merged_data.reference_id = reference_id ?? merged_data.reference_id ?? null;
	merged_data.created_at = created_at ?? merged_data.created_at ?? null;
	merged_data.updated_at = updated_at ?? merged_data.updated_at ?? null;
	merged_data.created_by_name = created_by_name ?? merged_data.created_by_name ?? null;
	merged_data.updated_by_name = updated_by_name ?? merged_data.updated_by_name ?? null;
	merged_data.is_sync = is_sync ?? merged_data.is_sync ?? false;

	assign(merged_data, rest_top_level_fields);

	return merged_data;
};
